import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime, Subject } from 'rxjs';
import { TabService } from 'src/app/shared/tab.service';
import { ordersService } from '../../services/orders.service';
import { CreateTmsOrderComponent } from '../tms-order/create-tms-order/create-tms-order.component';
import { CreatePtlOrderComponent } from '../ptl-order/create-ptl-order/create-ptl-order.component';

interface PtlStatusMeta {
  label: string;
  cssClass: string;
}

const PTL_STATUS_MAP: Record<string, PtlStatusMeta> = {
  PENDING_PICKUP: { label: 'Pending Pickup', cssClass: 'ptl-status-danger' },
  IN_TRANSIT:     { label: 'In Transit',     cssClass: 'ptl-status-warning' },
  DELIVERED:      { label: 'Delivered',      cssClass: 'ptl-status-success' },
};

@Component({
  selector: 'app-ptl-orders',
  templateUrl: './ptl-orders.component.html',
  styleUrls: ['./ptl-orders.component.css']
})
export class PtlOrdersComponent {
  theme = 'dark';

  // pagination / sorting
  pageSize = 50;
  pageNumber = 0;
  currentPage = 0;
  first = 0;
  sortingCol = 'id';
  direction = 'DESC';
  totalCount: any = 0;

  // data
  tableLists: any[] = [];
  filters: { key: string; value: any }[] = [];

  // user
  user_details: any;
  userType: any;
  roleNames: string[] = [];

  // dialogs
  ref: DynamicDialogRef | undefined;
  showDetailDialog = false;
  selectedOrder: any = null;
  sendingId: number | null = null;

  private getListTrigger$ = new Subject<void>();

  constructor(
    private ordersService: ordersService,
    private tabService: TabService,
    private router: Router,
    private messageService: MessageService,
    public dialogService: DialogService
  ) {
    this.getListTrigger$.pipe(debounceTime(0)).subscribe(() => this.fetchList());

    this.user_details = JSON.parse(localStorage.getItem('UserDetails')!);
    this.roleNames = (this.user_details?.userRoles || []).map((r: any) => r.name);
    this.userType = this.user_details?.userType;
  }

  ngOnInit(): void {
    this.getList();
  }

  // ───────── roles ─────────
  hasRole(...roles: string[]): boolean {
    return roles.some(r => this.roleNames.includes(r));
  }

  notRole(...roles: string[]): boolean {
    return !this.hasRole(...roles);
  }

  // ───────── list ─────────
  getList(): void {
    this.getListTrigger$.next();
  }

  private fetchList(): void {
    const postData = (this.filters || []).filter(
      (item: any) =>
        (item.value && item.value.toString().trim() !== '') ||
        (item.dateValue && item.dateValue.toString().trim() !== '') ||
        (item.dateTimeValue && item.dateTimeValue.toString().trim() !== '')
    );

    this.tableLists = [];
    this.ordersService
      .getPtlOrders(postData, this.pageNumber, this.pageSize, this.sortingCol, this.direction)
      .subscribe(
        (data: any) => {
          if (data) {
            this.tableLists = (data?.responseObject?.content || []).map((el: any) => ({
              ...el,
              route: this.buildRoute(el)
            }));
            this.totalCount = data?.responseObject?.totalElements;
          }
        },
        () => {
          this.totalCount = 0;
          this.tableLists = [];
        }
      );
  }

  private buildRoute(el: any): string {
    if (el?.fromAddress && el?.toAddress) {
      return `${el.fromAddress} → ${el.toAddress}`;
    }
    return '--';
  }

  onFilterChange(event: any): void {
    if (event) {
      this.filters = event;
      this.pageNumber = 0;
      this.currentPage = 0;
      this.first = 0;
      this.getList();
    }
  }

  onResetFilters(): void {
    this.filters = [];
    this.getList();
  }

  paginate(event: any): void {
    this.pageNumber = event.pageIndex;
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.first = event.pageIndex * event.pageSize;
    this.getList();
  }

  sortColumn(columnKey: string, sortOrder: 'ASC' | 'DESC'): void {
    this.sortingCol = columnKey;
    this.direction = sortOrder;
    this.getList();
  }

  // ───────── add / edit ─────────
  createPtlOrder(type: 'Add' | 'Edit', item?: any, url?:any): void {
    this.tabService.addTab({
      label: 'Pickup Request', path: url,
      fullPath: '',
      filterData: [],
    });
    this.router.navigate([url])

    // this.ref = this.dialogService.open(CreatePtlOrderComponent, {
    //   header: `${type} Order`,
    //   data: type === 'Add' ? {} : { editData: [item], edit: true },
    //   width: '70vw',
    //   contentStyle: { overflow: 'auto', position: 'top' },
    //   draggable: true,
    //   styleClass: 'no-close-icon-dialog',
    // });

    // this.ref.onClose.subscribe((data: any) => {
    //   if (data) {
    //     this.messageService.add({
    //       severity: 'success',
    //       summary: 'Success',
    //       detail: data?.responseMessage,
    //     });
    //   }
    //   this.getList();
    // });
  }

  editOrderRow(item: any): void {
    this.createPtlOrder('Edit', item);
  }

  // ───────── tabs (TabService) ─────────
  private openTab(label: string, path: string): void {
    this.tabService.addTab({
      label,
      path,
      fullPath: '',
      filterData: [],
    });
    this.router.navigate([path]);
  }

  trackDocket(item: any): void {
    this.showDetailDialog = false;
    this.openTab('Track Docket', 'tracking/track-docket/' + item?.docketNumber);
  }

  openPod(item: any): void {
    this.showDetailDialog = false;
    this.openTab('POD', 'quality/pod-audit/' + item?.id);
  }

  // ───────── send to vendor ─────────
  sendToVendor(item: any): void {
    this.sendingId = item?.id;
    this.ordersService.createUpdateOrder({ id: item?.id, status: 'SENT_TO_VENDOR' }).subscribe({
      next: (res: any) => {
        this.sendingId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res?.responseMessage || 'Order sent to vendor'
        });
        this.getList();
      },
      error: (err: any) => {
        this.sendingId = null;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Something went wrong'
        });
      }
    });
  }

  // ───────── detail popup ─────────
  openDetail(item: any): void {
    this.selectedOrder = item;
    this.showDetailDialog = true;
  }

  closeDetail(): void {
    this.showDetailDialog = false;
    this.selectedOrder = null;
  }

  // ───────── display helpers ─────────
  /** API has no status field yet — uses item.status when present, otherwise derives it. */
  getStatusKey(item: any): string {
    if (item?.status) {
      return String(item.status).toUpperCase().replace(/[-\s]/g, '_');
    }
    if (item?.podDocUrl) return 'DELIVERED';
    if (item?.docketNumber) return 'IN_TRANSIT';
    return 'PENDING_PICKUP';
  }

  getStatusLabel(item: any): string {
    return PTL_STATUS_MAP[this.getStatusKey(item)]?.label || this.getStatusKey(item);
  }

  getStatusClass(item: any): string {
    return PTL_STATUS_MAP[this.getStatusKey(item)]?.cssClass || 'ptl-status-default';
  }

  isAppointment(item: any): boolean {
    return (item?.deliveryType || '').toLowerCase() === 'appointment';
  }

  isCustomerCreated(item: any): boolean {
    return (item?.orderCreatedBy || '').toLowerCase() === 'customer';
  }

  displayName(item: any, nameKey: string, idKey: string): string {
    return item?.[nameKey] || (item?.[idKey] ? `#${item[idKey]}` : '--');
  }

  routeWithPin(item: any): string {
    const from = [item?.fromCityName || item?.fromAddress, item?.fromPincode].filter(Boolean).join(' — ');
    const to = [item?.toCityName || item?.toAddress, item?.toPincode].filter(Boolean).join(' — ');
    return from && to ? `${from} → ${to}` : '--';
  }
}